import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="1m791rdej2m29">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="6nmhmgviemb1">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="aq0ywwjj5e07" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="anwlnnzqr2n9" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="m5dx68xrfkud" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="25t5pz1tqo4fm" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="2kpen9xu2k7pe">
      <Navbar data-id="element-7" data-mp="2frv8mwtglswx" />
      <main data-id="element-8" data-mp="2fs4woeuy1pzz">
        <Hero data-id="element-9" data-mp="2fqk3otoa2f48" />
        <ConsularBanner data-id="element-10" data-mp="2fr3bhhk7rn4j" />
        <Mission data-id="element-11" data-mp="2fpiqaijshgjc" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-13" data-mp="2pgmv4pfr3l0g" />} data-id="element-12" data-mp="2ph50cbke59ki">
          <OurPeopleStories data-id="element-14" data-mp="2pig3qa9xcfb3" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-16" data-mp="33go5cvhwo4sl" />} data-id="element-15" data-mp="2pjiw6mmwapt4">
          <MakeDifferenceCTA data-id="element-17" data-mp="33hz7yrzm76i4" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-18" data-mp="33elbg0dmtril">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-19" data-mp="2yj5wf53w1pkl" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-21" data-mp="3i1jwndlwbpov" />} data-id="element-20" data-mp="3i464zfgm2985">
          <NewsBlog data-id="element-22" data-mp="3n2g0xsa5tdut" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-24" data-mp="3n1y5gxvbn3ny" />} data-id="element-23" data-mp="3n1ephmp8epck">
          <MediaScroller data-id="element-25" data-mp="385guw8uyht2g" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-27" data-mp="38574wl9wvlwr" />} data-id="element-26" data-mp="384nlt18iyrh5">
          <CommunityStrength data-id="element-28" data-mp="3d4jql2p1qmxr" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-30" data-mp="3d2p4hl21gos3" />} data-id="element-29" data-mp="3d38nl53fdj7p">
          <MissionPanels data-id="element-31" data-mp="2zd57kvv52lqp" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-33" data-mp="2zbkezbsdxcvh" />} data-id="element-32" data-mp="2zc3teiittj4r">
          <Leadership data-id="element-34" data-mp="2zbajj8okmdie" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-36" data-mp="345estylb6xmn" />} data-id="element-35" data-mp="344x03bxuv1jh">
          <AdvisoryCouncil data-id="element-37" data-mp="345okdqm05cug" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-39" data-mp="2pgacyo1zl5hp" />} data-id="element-38" data-mp="2pfqtv40lob23">
          <MakeDifferenceCTA data-id="element-40" data-mp="2pcuvjekubkd4" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-42" data-mp="2uetumc9jrv49" />} data-id="element-41" data-mp="2ud8zoljbma5v">
          <BusinessInvestments data-id="element-43" data-mp="2ubxviklyr0e8" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-45" data-mp="2gesx96shr5tj" />} data-id="element-44" data-mp="2gfc3vrcoy7s9">
          <ConsularAffairs data-id="element-46" data-mp="2ggn6hnueh9hs" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-48" data-mp="2kekzz2v7ehqz" />} data-id="element-47" data-mp="2kg4h0vooowtt">
          <SocialMediaVoices data-id="element-49" data-mp="2khz2qc7s4syy" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-51" data-mp="30uveunespakj" />} data-id="element-50" data-mp="30uc882uli8lt">
          <NonprofitDirectory data-id="element-52" data-mp="30t14u452b2v8" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-54" data-mp="2vwtwy5t3vhan" />} data-id="element-53" data-mp="2vxc25rxqx5up">
          <HondurasQuiz data-id="element-55" data-mp="2vug320a5wb4o" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-57" data-mp="2qvwdlpwd0h7k" />} data-id="element-56" data-mp="2qwekdggnedtq">
          <ExploreHonduras data-id="element-58" data-mp="2qzaip5wer4ip" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-60" data-mp="2lxbg3y8hsbmt" />} data-id="element-59" data-mp="2lyw7xg3f9ggz">
          <FAQ data-id="element-61" data-mp="2m07bbesygm7k" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-62" data-mp="2hwrheq89szyk">
        <Footer data-id="element-63" data-mp="2hy2kentw63om" />
      </Suspense>
      <Suspense fallback={null} data-id="element-64" data-mp="2d5sz5l3q1rso">
        <ChatbaseWidget data-id="element-65" data-mp="2d5j5nnjgx2i8" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [showLanding, setShowLanding] = useState(!isSectionAnchor(initialHash));
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-67" data-mp="1fgkmhr66fpb4" />} data-id="element-66" data-mp="1fezuo9b8ykgy">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-68" data-mp="1fhckt9r5s153" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-69" data-mp="msorcqw9a5gg">
        <Suspense fallback={<SectionLoader data-id="element-71" data-mp="11p65tqzqs078" />} data-id="element-70" data-mp="11oml62ipixpi">
          <ConsularPage data-id="element-72" data-mp="worvu3ekyg6k" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-73" data-mp="2d2ic08iz4uwk">
        <Suspense fallback={<SectionLoader data-id="element-75" data-mp="283f9ovudp2sa" />} data-id="element-74" data-mp="282vqlbszs8co">
          <BusinessMapPage data-id="element-76" data-mp="2869u4nde6nlp" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-77" data-mp="mlmrv51eytba">
        <Suspense fallback={<SectionLoader data-id="element-79" data-mp="qnhse7bit2o0" />} data-id="element-78" data-mp="mo0nbf00a7jd">
          <MembersDirectoryPage data-id="element-80" data-mp="qn8dbes0kfx3" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-81" data-mp="vndwe6k7wu3k">
        <Suspense fallback={<SectionLoader data-id="element-83" data-mp="10kmiujcefkam" />} data-id="element-82" data-mp="10m7c85mz8x6w">
          <VoicesOfImpactNYPage data-id="element-84" data-mp="10lfh0vxal1h5" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-85" data-mp="15ifnv91yu89b">
        <Suspense fallback={<SectionLoader data-id="element-87" data-mp="1adlhyv5iut8h" />} data-id="element-86" data-mp="1ac0n14fap8a3">
          <SaveTheDatePage data-id="element-88" data-mp="1ecuaxxsqmn2z" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-89" data-mp="1jcyi2x7ra5gj">
        <Suspense fallback={<SectionLoader data-id="element-91" data-mp="1ob09q6j8zd3m" />} data-id="element-90" data-mp="1jacef8nzka3l">
          <MemberProfilePage slug={memberSlug} data-id="element-92" data-mp="1ocbd458s6iu7" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-93" data-mp="1yd3zfzgtfdja">
        <Suspense fallback={<SectionLoader data-id="element-95" data-mp="1tfca9d7k25is" />} data-id="element-94" data-mp="1tfuccqgwfdym">
          <NewsStoryPage postId={newsPostId} data-id="element-96" data-mp="1ti6vh6ye7ud9" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-97" data-mp="1kfffquyhicft" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-98" data-mp="1kfxnanqlkd31">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-99" data-mp="1khs6nxm7zx50" />
      </LanguageProvider>
    </>;
}